import { useEffect, useMemo, useRef } from 'react';
import { playEvents } from '../audio/events';
import { holdFor } from '../lib/pace';
import { musicEvents, play } from '../audio/sound';
import { activePlayer } from '@estandarte/engine';
import { ofType } from '../lib/actions';
import { lootStep } from '../lib/lootTargets';
import { useGame } from '../store/game';
import { useUi } from '../store/ui';
import { MapView, type TargetKind } from './MapView';
import { BossMapCard, Hud } from './Hud';
import { MapViewport } from './MapViewport';
import { theme } from '../theme';
import { EndScreen, MissionModal, PassScreen, ReactionPrompt } from './Overlays';

export function GameScreen() {
  const { view, me, legal, dispatch, needsPass, lastEvents, eventSeq, error, setError, reaction, fastBots } = useGame();
  const ui = useUi();

  // Sons dos eventos novos (não toca de novo o que já estava na tela ao abrir).
  const playedSeq = useRef(eventSeq);
  useEffect(() => {
    if (eventSeq === playedSeq.current || !view) return;
    playedSeq.current = eventSeq;
    holdFor(playEvents(lastEvents, view, me, { fastBots }));
    musicEvents(lastEvents);
  }, [eventSeq, lastEvents, view, me, fastBots]);

  // Limpa a seleção quando muda o jogador ou a fase.
  const phaseKey = `${view?.current}-${view?.phase}-${view?.pending?.kind ?? ''}`;
  useEffect(() => {
    useUi.getState().reset();
  }, [phaseKey]);

  // Some com a seleção que deixou de ser válida (ex.: origem ficou com 1 tropa); fecha a carta que saiu da mão.
  useEffect(() => {
    const st = useUi.getState();
    if (st.selected && !legal.some((a) => 'from' in a && a.from === st.selected)) st.select(null);
    if (st.loot && !legal.some((a) => a.type === 'playLoot' && a.kind === st.loot)) st.startLoot(null);
  }, [legal]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 3500);
    return () => clearTimeout(t);
  }, [error, setError]);

  const step = useMemo(
    () => (view && me !== null && ui.loot ? lootStep(view, me, legal, ui.loot, ui.picks) : null),
    [view, me, legal, ui.loot, ui.picks],
  );

  const interaction = useMemo(() => {
    const clickable = new Set<string>();
    const targets = new Set<string>();
    let kind: TargetKind = 'own';
    let bossTargetable = false;
    const pending = view?.pending;

    if (pending?.kind === 'heal') {
      for (const a of ofType(legal, 'heal')) clickable.add(a.territory), targets.add(a.territory);
      return { clickable, targets, kind, bossTargetable };
    }
    if (step) {
      kind = ui.loot === 'strike' || ui.loot === 'paralyze' || ui.loot === 'barrage' ? 'attack' : ui.loot === 'teleport' || ui.loot === 'wall' ? 'move' : 'own';
      for (const t of step.clickable) clickable.add(t), targets.add(t);
      return { clickable, targets, kind, bossTargetable };
    }
    if (pending) return { clickable, targets, kind, bossTargetable };

    const places = ofType(legal, 'place');
    if (places.length) {
      for (const a of places) clickable.add(a.territory);
      return { clickable, targets, kind, bossTargetable };
    }
    if (view?.phase === 'attack') {
      kind = 'attack';
      for (const a of ofType(legal, 'attack')) {
        clickable.add(a.from);
        clickable.add(a.to);
        if (a.from === ui.selected) targets.add(a.to);
      }
      for (const a of ofType(legal, 'attackBoss')) {
        clickable.add(a.from);
        bossTargetable = true;
      }
    }
    if (view?.phase === 'fortify') {
      kind = 'move';
      for (const a of ofType(legal, 'fortify')) {
        clickable.add(a.from);
        if (a.from === ui.selected) {
          targets.add(a.to);
          clickable.add(a.to);
        }
      }
    }
    return { clickable, targets, kind, bossTargetable };
  }, [legal, view, ui.selected, step, ui.loot]);

  if (!view) return null;

  function onTerritory(id: string) {
    const st = useUi.getState();
    if (view!.pending?.kind === 'heal') {
      const a = ofType(legal, 'heal').find((x) => x.territory === id);
      if (a) dispatch(a);
      return;
    }
    if (step) {
      const r = step.pick(id);
      if (r.action) {
        dispatch(r.action);
        st.startLoot(null);
      } else if (r.picks) st.setPicks(r.picks);
      return;
    }
    const places = ofType(legal, 'place').filter((a) => a.territory === id);
    if (places.length) {
      const max = Math.max(...places.map((a) => a.count));
      const want = st.amount === 'all' ? max : Math.min(st.amount, max);
      const a = places.find((x) => x.count === want);
      if (a) dispatch(a);
      return;
    }
    if (view!.phase === 'attack' || view!.phase === 'fortify') play('selecionar', { bus: 'ui' });
    if (view!.phase === 'attack') {
      const attacks = ofType(legal, 'attack');
      const fromHere = attacks.some((a) => a.from === id) || ofType(legal, 'attackBoss').some((a) => a.from === id);
      if (st.selected && attacks.some((a) => a.from === st.selected && a.to === id)) {
        st.setTarget(id);
        return;
      }
      if (fromHere) {
        st.select(st.selected === id ? null : id);
        return;
      }
      // Clicou num alvo sem origem: escolhe a origem mais forte.
      const sources = attacks.filter((a) => a.to === id).map((a) => a.from);
      if (sources.length) {
        const best = sources.sort((a, b) => view!.territories[b].troops - view!.territories[a].troops)[0];
        st.select(best);
        useUi.getState().setTarget(id);
      }
      return;
    }
    if (view!.phase === 'fortify') {
      const moves = ofType(legal, 'fortify');
      if (st.selected && moves.some((a) => a.from === st.selected && a.to === id)) {
        st.setTarget(id);
        return;
      }
      if (moves.some((a) => a.from === id)) st.select(st.selected === id ? null : id);
    }
  }

  function onLair() {
    const st = useUi.getState();
    const froms = ofType(legal, 'attackBoss').map((a) => a.from);
    if (!froms.length) return;
    if (!st.selected || !froms.includes(st.selected)) {
      const best = froms.sort((a, b) => view!.territories[b].troops - view!.territories[a].troops)[0];
      st.select(best);
    }
    useUi.getState().setBossTarget(true);
  }

  const active = activePlayer(view);
  const selected = step ? (ui.loot === 'barrage' || ui.loot === 'teleport' || ui.loot === 'wall' ? ui.picks[0] ?? null : null) : ui.selected;
  const [mapW, mapH] = theme.viewBox;
  return (
    <div className="game">
      <MapViewport
        width={mapW}
        height={mapH}
        overlay={(project, v) =>
          view.boss ? (
            <BossMapCard view={view} project={project} scale={v.k} canAttack={interaction.bossTargetable} onAttack={onLair} />
          ) : null
        }
      >
        <MapView
          view={view}
          selected={selected}
          target={step ? (ui.loot === 'teleport' ? ui.picks[1] ?? null : null) : ui.target}
          targets={interaction.targets}
          targetKind={interaction.kind}
          clickable={interaction.clickable}
          marked={ui.loot === 'barrage' ? step?.marked : undefined}
          bossTargetable={interaction.bossTargetable}
          bossSelected={ui.bossTarget}
          onTerritory={onTerritory}
          onLair={onLair}
          events={lastEvents}
          eventSeq={eventSeq}
          title={`Mapa — rodada ${view.round}`}
          bossLabel={false}
        />
      </MapViewport>
      <Hud step={step} />
      {error && (
        <div className="toast error" role="alert">
          {error}
        </div>
      )}
      {needsPass !== null && <PassScreen player={needsPass} />}
      {reaction && needsPass === null && <ReactionPrompt />}
      {view.phase === 'over' && <EndScreen />}
      {ui.showMission && <MissionModal />}
      <span className="visually-hidden" aria-live="polite">
        {active !== null && active >= 0 ? `Vez de ${view.players[active].name}` : ''}
      </span>
    </div>
  );
}
